import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import toast from 'react-hot-toast';
import { HeldFile } from '../../types/task';
import { tasksApi } from '../../services/api/tasks';
import { Button } from '../shared/Button';

// Stable empty reference so the useMemo below is not invalidated on every render.
const NO_HELD_FILES: HeldFile[] = [];
import { formatRelativeTime, formatDateTime, activeTimezone } from '../../utils/formatDateTime';

/**
 * THUB-152: held-file review before re-enabling a task.
 *
 * Replaces the all-or-nothing `window.confirm` from THUB-149 ("this will deliver N held
 * file(s)"), which gave an operator no way to see WHAT was queued or to drop a stale/
 * accidental drop. Here they can review each held file, discard the ones that should not
 * go out, and enable delivering only the rest.
 *
 * ENABLE IS THE DECISION POINT FOR THE WHOLE QUEUE. An unselected file is not deferred to
 * some later enable — it is removed from the delivery queue when the operator enables.
 *
 * This changed deliberately. Unchecking used to leave the row 'held', which read as "I'll
 * deal with it next time" but actually meant FOREVER: the held-files table has TTL enabled
 * on a `ttl` attribute that nothing ever writes, so no row is ever reaped (0 of 56 rows
 * across DEV/NP/PRD had one). Those rows stayed `deliverable`, so a "deliver all" months
 * later would ship a stale file to a partner with no signal it was stale. An indefinite
 * queue with no expiry is worse than an explicit removal.
 *
 * What "removed from the queue" means, precisely — this is NOT data loss:
 *   - only the held POINTER row is deleted; the archived bytes stay at archiveKey under the
 *     bucket's retention policy, still listable and downloadable from File History;
 *   - a HELD_FILE_DISCARDED event is written against the file's fileId, so the journey reads
 *     `... -> FILE_HELD -> HELD_FILE_DISCARDED` and records who did it and when.
 * Every string in this component pairs the two halves, because "discarded" alone reads as
 * "destroyed" to an operator and that misreading is the expensive one.
 *
 * Two routes to the same outcome, both explicitly confirmed:
 *   - "Discard unselected" — do it now, before enabling.
 *   - "Enable & deliver"   — anything unselected is removed as part of the enable.
 * Neither is implicit: both warn first, because the queue is the one thing an operator
 * cannot reconstruct from the archive.
 *
 * ONE EXCEPTION: non-deliverable rows (with_metadata, cold storage, missing archive) are
 * NEVER auto-removed. Their checkbox is disabled, so they are unselected through no choice
 * of the operator's — sweeping them up would destroy the very with_metadata files the UI
 * just said to send manually (THUB-42). They stay held and individually discardable.
 */

export interface HeldFilesReviewProps {
  taskId: string;
  taskName?: string;
  /** Called with the selected heldFileIds when the operator confirms the enable. */
  onConfirm: (selectedHeldFileIds: string[]) => void | Promise<void>;
  onCancel: () => void;
  /** Test seam: skip the network fetch and render these rows directly. */
  initialHeldFiles?: HeldFile[];
}

function formatFileSize(bytes?: number): string {
  if (bytes === undefined || bytes === null) return '-';
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${units[i]}`;
}

/** Human-readable why-is-this-not-deliverable, or null when it is deliverable. */
function blockedReason(file: HeldFile): string | null {
  if (file.withMetadata) return 'Requires metadata — send manually after enabling';
  if (file.archiveMissing) return 'Archived copy is missing';
  if (file.inColdStorage) return `Archive in cold storage (${file.storageClass})`;
  return null;
}

export function HeldFilesReview({
  taskId,
  taskName,
  onConfirm,
  onCancel,
  initialHeldFiles,
}: HeldFilesReviewProps) {
  // THUB-299: the read was three useState values plus a `load` callback and an effect with an
  // exhaustive-deps disable pinning it to mount.
  //
  // The snapshot semantics are DELIBERATE and preserved: this is a modal the operator makes a
  // decision in, so rows must not shift under their checkboxes mid-decision. staleTime Infinity
  // plus gcTime 0 gives exactly the old behaviour -- one read per open, no background refetch,
  // and a fresh read on the next open rather than a stale cached snapshot.
  const heldFilesQuery = useQuery({
    // Keyed OUTSIDE the ['tasks'] prefix on purpose -- see queryKeys.heldFiles. Under it, the
    // central execution_updates invalidation refetched this modal mid-decision and the newly
    // arrived rows came back pre-selected, because selection is derived. staleTime alone does
    // not stop invalidateQueries from refetching an active observer.
    queryKey: queryKeys.heldFiles(taskId),
    queryFn: () => tasksApi.getHeldFiles(taskId),
    // Callers that already hold the rows (the enable flow passes them in) must not re-read.
    enabled: initialHeldFiles === undefined,
    staleTime: Infinity,
    gcTime: 0,
  });

  // Discards are expressed as a delta over the server rows instead of mutating a local copy of
  // the list. That is what lets the prop-supplied and fetched paths share one code path -- the
  // old code could only remove rows because it had copied them into state first.
  const [discardedIds, setDiscardedIds] = useState<Set<string>>(new Set());

  // Selection is DERIVED, not seeded. Every deliverable row starts selected (per the ticket: the
  // common case is "deliver what accumulated"), and this tracks only what the operator actively
  // unchecked. Seeding a Set from freshly-arrived data is what forced the old set-state-on-load
  // shape; a delta needs no such step, and non-deliverable rows are unselectable by construction
  // rather than by remembering to filter them at seed time.
  const [deselectedIds, setDeselectedIds] = useState<Set<string>>(new Set());

  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const sourceRows = initialHeldFiles ?? heldFilesQuery.data?.heldFiles ?? NO_HELD_FILES;
  const heldFiles = useMemo(
    () => sourceRows.filter(f => !discardedIds.has(f.heldFileId)),
    [sourceRows, discardedIds]
  );
  const loading = initialHeldFiles === undefined && heldFilesQuery.isPending;
  const error = heldFilesQuery.error ? 'Failed to load held files' : null;

  const deliverable = useMemo(() => heldFiles.filter(f => f.deliverable), [heldFiles]);
  const selectedIds = useMemo(
    () =>
      new Set(
        deliverable.filter(f => !deselectedIds.has(f.heldFileId)).map(f => f.heldFileId)
      ),
    [deliverable, deselectedIds]
  );
  const withMetadataFiles = useMemo(() => heldFiles.filter(f => f.withMetadata), [heldFiles]);
  const selectedDeliverableCount = useMemo(
    () => deliverable.filter(f => selectedIds.has(f.heldFileId)).length,
    [deliverable, selectedIds]
  );
  const allDeliverableSelected =
    deliverable.length > 0 && selectedDeliverableCount === deliverable.length;

  const toggleOne = useCallback((heldFileId: string) => {
    // Toggling the DESELECTED set is the inverse of toggling a selected set, and cannot drift
    // out of sync with the rows the way a separately-held selection Set could.
    setDeselectedIds(prev => {
      const next = new Set(prev);
      if (next.has(heldFileId)) {
        next.delete(heldFileId);
      } else {
        next.add(heldFileId);
      }
      return next;
    });
  }, []);

  const toggleAll = useCallback(() => {
    // Only ever selects DELIVERABLE rows — selecting an excluded file would set an expectation
    // the backend will not honour. Deselecting "all" therefore means deselecting exactly the
    // deliverable rows, which is what the derived selection reads back as empty.
    setDeselectedIds(prev => {
      const allSelected = deliverable.every(f => !prev.has(f.heldFileId));
      return allSelected ? new Set(deliverable.map(f => f.heldFileId)) : new Set();
    });
  }, [deliverable]);

  const discardOne = useCallback(async (file: HeldFile) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(
      `Discard "${file.filename}"?\n\n`
      + 'It will NOT be delivered to the partner. The archived copy is kept '
      + '(retention policy governs it), but the queued delivery is removed. '
      + 'This is recorded in the file\'s history.'
    )) {
      return;
    }
    setBusyIds(prev => new Set(prev).add(file.heldFileId));
    try {
      await tasksApi.discardHeldFile(taskId, file.heldFileId);
      // Dropping the row also drops it from the derived selection -- no separate
      // setSelectedIds step to forget.
      setDiscardedIds(prev => new Set(prev).add(file.heldFileId));
      toast.success(`Discarded ${file.filename}`);
    } catch (e) {
      console.error('Failed to discard held file:', e);
      toast.error(`Failed to discard ${file.filename}`);
    } finally {
      setBusyIds(prev => {
        const next = new Set(prev);
        next.delete(file.heldFileId);
        return next;
      });
    }
  }, [taskId]);

  // Files the operator ACTIVELY unchecked. Non-deliverable rows (with_metadata, cold
  // storage, missing archive) render with a DISABLED checkbox, so they are permanently
  // "unselected" through no choice of the operator's — sweeping them into a bulk discard
  // would silently destroy the very with_metadata files the callout just told them to send
  // manually. They stay individually discardable via the per-row Discard button, which is
  // an unambiguous expression of intent.
  const unselectedDeliverable = useMemo(
    () => deliverable.filter(f => !selectedIds.has(f.heldFileId)),
    [deliverable, selectedIds]
  );

  const discardUnselected = useCallback(async () => {
    const targets = unselectedDeliverable;
    if (targets.length === 0) return;
    // Bulk discard is destructive and irreversible for the queued delivery — always
    // confirm, per the ticket's RBAC/confirmation requirement.
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(
      `Discard ${targets.length} unselected held file(s)?\n\n`
      + 'They will NOT be delivered to the partner. Archived copies are kept '
      + '(retention policy governs them), but the queued deliveries are removed. '
      + 'Each discard is recorded in that file\'s history.'
    )) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await tasksApi.discardHeldFiles(taskId, {
        heldFileIds: targets.map(f => f.heldFileId),
      });
      const discardedSet = new Set(result.discardedIds || []);
      setDiscardedIds(prev => {
        const next = new Set(prev);
        discardedSet.forEach(id => next.add(id as string));
        return next;
      });
      if (result.failedIds?.length) {
        toast.error(
          `Discarded ${result.discarded}; ${result.failedIds.length} failed — refresh and retry`
        );
      } else {
        toast.success(`Discarded ${result.discarded} held file(s)`);
      }
    } catch (e) {
      console.error('Failed to discard held files:', e);
      toast.error('Failed to discard held files');
    } finally {
      setSubmitting(false);
    }
  }, [unselectedDeliverable, taskId]);

  const handleConfirm = useCallback(async () => {
    // THUB-152: enabling now REMOVES every unselected file from the delivery queue, so this
    // click is destructive to the queue even when the operator only meant "deliver these
    // two". Confirm it explicitly — the same standard already applied to "Discard
    // unselected", which does the identical thing by a different route. Without this, an
    // operator who unchecks a row to "come back to it later" loses the queued delivery
    // silently, and the queue is the one thing they cannot reconstruct from the archive.
    if (unselectedDeliverable.length > 0) {
      const ok = window.confirm(
        `Enable and deliver ${selectedDeliverableCount} file(s)?\n\n`
        + `${unselectedDeliverable.length} unselected file(s) will be REMOVED from the `
        + 'delivery queue and will not be delivered now or later.\n\n'
        + 'The archived copies are kept for audit and stay downloadable from File History. '
        + 'Each removal is recorded in that file\'s history.'
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      // Send only the selected DELIVERABLE ids. Sending an excluded id would be honest
      // about intent but the backend would skip it anyway, and it would inflate the
      // "selected" count the operator sees in the result toast.
      await onConfirm(deliverable.filter(f => selectedIds.has(f.heldFileId)).map(f => f.heldFileId));
    } finally {
      setSubmitting(false);
    }
  }, [deliverable, selectedIds, onConfirm, unselectedDeliverable, selectedDeliverableCount]);

  const tz = activeTimezone();

  return (
    <div className="flex flex-col max-h-[80vh]" data-testid="held-files-review">
      <div className="px-6 py-4 border-b border-neutral-200 dark:border-slate-700">
        <h2 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
          Review held files
        </h2>
        <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
          {taskName
            ? <>These files accumulated while <span className="font-medium">{taskName}</span> was disabled. </>
            : 'These files accumulated while the task was disabled. '}
          Only the files you select will be delivered when you enable it. Unselected files
          stay held; use Discard to remove them permanently.
        </p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-4">
        {loading && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">Loading held files…</p>
        )}

        {!loading && error && (
          <div className="flex items-center justify-between gap-4 rounded-md bg-danger-50 dark:bg-danger-900/20 border border-danger-300 dark:border-danger-700 px-3 py-2">
            <span className="text-sm text-danger-700 dark:text-danger-400">{error}</span>
            <Button variant="secondary" size="sm" onClick={() => heldFilesQuery.refetch()}>Retry</Button>
          </div>
        )}

        {!loading && !error && heldFiles.length === 0 && (
          <p className="text-sm text-neutral-500 dark:text-neutral-400">
            No files are currently held for this task.
          </p>
        )}

        {!loading && !error && heldFiles.length > 0 && (
          <>
            {/* with_metadata callout — carried over from the THUB-149 confirm text. */}
            {withMetadataFiles.length > 0 && (
              <div className="mb-4 rounded-md bg-warning-50 dark:bg-warning-900/20 border border-warning-300 dark:border-warning-700 px-3 py-2">
                <p className="text-sm text-warning-800 dark:text-warning-300">
                  {withMetadataFiles.length} file(s) require metadata and cannot be
                  auto-delivered. Send them manually via the transfer dialog after enabling.
                </p>
              </div>
            )}

            <div className="mb-3 flex items-center justify-between gap-3">
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                <input
                  type="checkbox"
                  checked={allDeliverableSelected}
                  onChange={toggleAll}
                  disabled={deliverable.length === 0}
                  aria-label="Select all deliverable held files"
                  className="rounded border-neutral-300 dark:border-slate-600"
                />
                Select all deliverable ({deliverable.length})
              </label>
              <span className="text-sm text-neutral-500 dark:text-neutral-400" data-testid="held-selection-summary">
                {selectedDeliverableCount} of {deliverable.length} selected
              </span>
            </div>

            <ul className="divide-y divide-neutral-200 dark:divide-slate-700 border border-neutral-200 dark:border-slate-700 rounded-md">
              {heldFiles.map(file => {
                const blocked = blockedReason(file);
                const busy = busyIds.has(file.heldFileId);
                return (
                  <li
                    key={file.heldFileId}
                    className="flex items-start gap-3 px-3 py-2.5"
                    data-testid={`held-file-row-${file.heldFileId}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 rounded border-neutral-300 dark:border-slate-600"
                      checked={selectedIds.has(file.heldFileId)}
                      onChange={() => toggleOne(file.heldFileId)}
                      disabled={!file.deliverable || busy}
                      aria-label={`Deliver ${file.filename}`}
                    />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                        {file.filename}
                      </p>
                      <p className="mt-0.5 text-xs text-neutral-500 dark:text-neutral-400">
                        {formatFileSize(file.fileSize)}
                        {' · held '}
                        <span title={formatDateTime(file.heldAt, tz, 'full')}>
                          {formatRelativeTime(file.heldAt, tz)}
                        </span>
                        {file.reason ? ` · ${file.reason}` : ''}
                        {` · ${file.transferMethod}`}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-neutral-400 dark:text-neutral-500" title={file.archiveLocation}>
                        {file.archiveLocation}
                      </p>
                      {blocked && (
                        <p className="mt-1 text-xs text-warning-700 dark:text-warning-400">
                          {blocked}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => discardOne(file)}
                      isLoading={busy}
                      disabled={busy || submitting}
                      aria-label={`Discard ${file.filename}`}
                    >
                      Discard
                    </Button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>

      {/* THUB-152: enabling is the decision point for the whole queue — anything left
          unselected is removed from it. That is a non-obvious consequence of clicking
          Enable, so state it up front instead of letting the operator discover it from a
          toast afterwards. Always paired with the archive reassurance: "removed from the
          queue" is not "deleted", and an operator who reads only half of that will assume
          the file is gone. */}
      {unselectedDeliverable.length > 0 && (
        <div className="px-6 pb-2">
          <p className="text-sm text-amber-700 dark:text-amber-400">
            {unselectedDeliverable.length} unselected file(s) will be{' '}
            <strong>removed from the delivery queue</strong> when you enable — they will not
            be delivered now or later. The archived copies are kept for audit and stay
            downloadable from File History.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2 px-6 py-4 border-t border-neutral-200 dark:border-slate-700">
        <Button variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        {unselectedDeliverable.length > 0 && (
          <Button
            variant="danger"
            onClick={discardUnselected}
            disabled={submitting || loading}
          >
            Discard unselected ({unselectedDeliverable.length})
          </Button>
        )}
        <Button
          variant="primary"
          onClick={handleConfirm}
          isLoading={submitting}
          disabled={submitting || loading}
        >
          {/* THUB-152: with nothing selected, enabling clears the queue rather than merely
              "not delivering", so the label has to say so — "Enable without delivering"
              reads as a no-op and this is not one. */}
          {selectedDeliverableCount > 0
            ? `Enable & deliver ${selectedDeliverableCount} file(s)`
            : unselectedDeliverable.length > 0
              ? `Enable & clear queue (${unselectedDeliverable.length})`
              : 'Enable'}
        </Button>
      </div>
    </div>
  );
}

export default HeldFilesReview;
